/**
 * EOS Survey Service
 *
 * Provides CRUD operations for the End-of-Season 2026 survey.
 * Data lives in the `eos-surveys` Firestore collection.
 *
 * @module EosSurveyService
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { EosSurveySubmission } from '../@types/eosSurvey';

const COLLECTION = 'eos-surveys';

export class EosSurveyService {
  /**
   * Submit a new EOS survey.
   * Returns the newly-created document ID.
   */
  static async submitSurvey(
    data: Omit<EosSurveySubmission, 'id' | 'submittedAt'>
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COLLECTION), {
        ...data,
        submittedAt: Timestamp.now(),
      });
      console.log('[EosSurvey] Survey submitted:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('[EosSurvey] Error submitting survey:', error);
      throw new Error(`Failed to submit survey: ${error.message}`);
    }
  }

  /**
   * Fetch all submissions (managers).
   * Ordered newest-first.
   */
  static async getAllSubmissions(): Promise<EosSurveySubmission[]> {
    try {
      const q = query(
        collection(db, COLLECTION),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as EosSurveySubmission[];
    } catch (error: any) {
      console.error('[EosSurvey] Error fetching submissions:', error);
      throw new Error(`Failed to fetch submissions: ${error.message}`);
    }
  }

  /**
   * Fetch submissions for a specific user (to enforce one-time submission).
   */
  static async getSubmissionsByUser(
    userId: string
  ): Promise<EosSurveySubmission[]> {
    try {
      const q = query(
        collection(db, COLLECTION),
        where('submittedBy', '==', userId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as EosSurveySubmission[];
    } catch (error: any) {
      console.error('[EosSurvey] Error fetching user submissions:', error);
      throw new Error(`Failed to fetch user submissions: ${error.message}`);
    }
  }
}
